// @ts-strict-ignore
import {Component, Injector, OnInit} from "@angular/core";
import {
	BasicMaterialDialog,
	MatDialogDataBaseInput,
	MatDialogDataBaseOutput,
} from "../../basic-material-dialog/basic-material-dialog.directive";
import Enumerable from "linq";
import {SimEnumExtendedService, NotificationService, SimSystemExtendedService, SimTrainingRoomService} from "@services";
import {PlayerMapping, PlayerType, TrainingRoom} from "@models";
import {Translations} from "@translations";

export interface DialogData_RoomPlayerSelectionDialogInput extends MatDialogDataBaseInput {
	trainingRoom: TrainingRoom;
}

export interface DialogData_RoomPlayerSelectionDialogOutput extends MatDialogDataBaseOutput {
	updatedTrainingRoom: TrainingRoom;
	updatedDebriefingPlayers: PlayerMapping[];
	updatedMediaScreens: PlayerMapping[];
	updatedDigitalSignagePlayers: PlayerMapping[];
}

@Component({
	selector: "sim-room-player-selection-dialog",
	templateUrl: "./room-player-selection-dialog.component.html",
	styleUrls: ["./room-player-selection-dialog.component.scss"],
	standalone: false,
})
export class RoomPlayerSelectionDialogComponent extends BasicMaterialDialog<
	RoomPlayerSelectionDialogComponent,
	DialogData_RoomPlayerSelectionDialogInput,
	DialogData_RoomPlayerSelectionDialogOutput
> {
	T = Translations;
	public selectedDebriefingPlayer: PlayerMapping = null;
	public selectedMediaScreenPlayer: PlayerMapping = null;
	public selectedDigitalSignagePlayer: PlayerMapping = null;

	public playerList: Enumerable.IDictionary<string, PlayerMapping> = null;
	public availableDebriefingPlayerList: PlayerMapping[] = null;
	public usedDebriefingPlayerList: PlayerMapping[] = null;
	public availableMediaScreenPlayerList: PlayerMapping[] = null;
	public usedMediaScreenPlayerList: PlayerMapping[] = null;
	public availableDigitalSignagePlayerList: PlayerMapping[] = null;
	public usedDigitalSignagePlayerList: PlayerMapping[] = null;
	public finalDebriefingPlayers: PlayerMapping[] = [];
	public finalMediaScreens: PlayerMapping[] = [];
	public finalDigitalSignagePlayers: PlayerMapping[] = [];

	constructor(
		_injector: Injector,
		private _simSystemService: SimSystemExtendedService,
		private _simEnumService: SimEnumExtendedService,
		private _simTrainingRoomService: SimTrainingRoomService,
		private _simNotificationService: NotificationService,
	) {
		super(_injector);
	}

	public ngOnInit() {
		super.ngOnInit();

		this._simSystemService.getPlayers().then((result) => {
			this.playerList = Enumerable.from(result).toDictionary(
				(player) => player.id,
				(player) => player,
			);
		});
	}

	ok() {
		this.selectedDebriefingPlayer = null;
		this.availableDebriefingPlayerList = null;
		this.usedDebriefingPlayerList = null;
		this.selectedMediaScreenPlayer = null;
		this.availableMediaScreenPlayerList = null;
		this.usedMediaScreenPlayerList = null;
		this.selectedDigitalSignagePlayer = null;
		this.availableDigitalSignagePlayerList = null;
		this.usedDigitalSignagePlayerList = null;
		this.close({
			action: "ok",
			updatedTrainingRoom: this.data.trainingRoom,
			updatedDebriefingPlayers: this.finalDebriefingPlayers,
			updatedMediaScreens: this.finalMediaScreens,
			updatedDigitalSignagePlayers: this.finalDigitalSignagePlayers,
		});
	}

	//Debriefing Players
	public filterAvailableDebriefingPlayerList() {
		if (!!this.playerList && !!this.data?.trainingRoom?.debriefingPlayerDeviceIds) {
			this.availableDebriefingPlayerList = this.playerList
				.toEnumerable()
				.where(
					(player) =>
						!this.data.trainingRoom.debriefingPlayerDeviceIds.includes(player.value.id) &&
						this._simEnumService.containsFlags(player.value.type, PlayerType.Debriefing),
				)
				.select((r) => r.value)
				.toArray();
		}
		return this.availableDebriefingPlayerList;
	}

	public filterUsedDebriefingPlayerList() {
		if (!!this.playerList && !!this.data?.trainingRoom?.debriefingPlayerDeviceIds) {
			this.usedDebriefingPlayerList = this.playerList
				.toEnumerable()
				.where((player) => this.data.trainingRoom.debriefingPlayerDeviceIds.includes(player.value.id))
				.select((r) => r.value)
				.toArray();
		}

		if (this.usedDebriefingPlayerList) {
			this.finalDebriefingPlayers = this.usedDebriefingPlayerList;
		}
		return this.usedDebriefingPlayerList;
	}

	public addDebriefingPlayer(player: PlayerMapping) {
		if (this.data.trainingRoom.debriefingPlayerDeviceIds.indexOf(player.id) < 0) {
			this.data.trainingRoom.debriefingPlayerDeviceIds.push(player.id);
			this.finalDebriefingPlayers.push(player);
			/*this.data.trainingRoom.debriefingPlayers.push(player);*/
			this.resetFilters();
		}
	}

	public removeDebriefingPlayer(player: PlayerMapping) {
		this.data.trainingRoom.debriefingPlayerDeviceIds.splice(this.data.trainingRoom.debriefingPlayerDeviceIds.indexOf(player.id), 1);
		this.finalDebriefingPlayers.splice(this.finalDebriefingPlayers.indexOf(player), 1);
		/*this.data.trainingRoom.debriefingPlayers.splice(this.data.trainingRoom.debriefingPlayers.indexOf(player), 1);*/
		this.resetFilters();
	}

	//Media Screens
	public filterAvailableMediaScreenPlayerList() {
		if (!!this.playerList && !!this.data?.trainingRoom?.mediaScreenDeviceIds) {
			this.availableMediaScreenPlayerList = this.playerList
				.toEnumerable()
				.where(
					(player) =>
						!this.data.trainingRoom.mediaScreenDeviceIds.includes(player.value.id) &&
						this._simEnumService.containsFlags(player.value.type, PlayerType.MediaScreen),
				)
				.select((r) => r.value)
				.toArray();
		}

		return this.availableMediaScreenPlayerList;
	}

	public filterUsedMediaScreenPlayerList() {
		if (!!this.playerList && !!this.data?.trainingRoom?.mediaScreenDeviceIds) {
			this.usedMediaScreenPlayerList = this.playerList
				.toEnumerable()
				.where((player) => this.data.trainingRoom.mediaScreenDeviceIds.includes(player.value.id))
				.select((r) => r.value)
				.toArray();
		}

		if (this.usedMediaScreenPlayerList) {
			this.finalMediaScreens = this.usedMediaScreenPlayerList;
		}
		return this.usedMediaScreenPlayerList;
	}

	public addMediaScreenPlayer(player: PlayerMapping) {
		if (this.data.trainingRoom.mediaScreenDeviceIds.indexOf(player.id) < 0) {
			this.data.trainingRoom.mediaScreenDeviceIds.push(player.id);
			this.finalMediaScreens.push(player);
			/*this.data.trainingRoom.mediaScreens.push(player);*/
			this.resetFilters();
		}
	}

	public removeMediaScreenPlayer(player: PlayerMapping) {
		this.data.trainingRoom.mediaScreenDeviceIds.splice(this.data.trainingRoom.mediaScreenDeviceIds.indexOf(player.id), 1);
		this.finalMediaScreens.splice(this.finalMediaScreens.indexOf(player), 1);
		/*this.data.trainingRoom.mediaScreens.splice(this.data.trainingRoom.mediaScreens.indexOf(player), 1);*/
		this.resetFilters();
	}

	//Digital Signage
	public filterAvailableDigitalSignagePlayerList() {
		if (!!this.playerList && !!this.data?.trainingRoom?.digitalSignageDeviceIds) {
			this.availableDigitalSignagePlayerList = this.playerList
				.toEnumerable()
				.where(
					(player) =>
						!this.data.trainingRoom.digitalSignageDeviceIds.includes(player.value.id) &&
						this._simEnumService.containsFlags(player.value.type, PlayerType.DigitalSignage),
				)
				.select((r) => r.value)
				.toArray();
		}

		return this.availableDigitalSignagePlayerList;
	}

	public filterUsedDigitalSignagePlayerList() {
		if (!!this.playerList && !!this.data?.trainingRoom?.digitalSignageDeviceIds) {
			this.usedDigitalSignagePlayerList = this.playerList
				.toEnumerable()
				.where((player) => this.data.trainingRoom.digitalSignageDeviceIds.includes(player.value.id))
				.select((r) => r.value)
				.toArray();
		}

		if (this.usedDigitalSignagePlayerList) {
			this.finalDigitalSignagePlayers = this.usedDigitalSignagePlayerList;
		}
		return this.usedDigitalSignagePlayerList;
	}

	public addDigitalSignagePlayer(player: PlayerMapping) {
		if (this.data.trainingRoom.digitalSignageDeviceIds.indexOf(player.id) < 0) {
			this.data.trainingRoom.digitalSignageDeviceIds.push(player.id);
			this.finalDigitalSignagePlayers.push(player);
			/*this.data.trainingRoom.digitalSignagePlayers.push(player);*/
			this.resetFilters();
		}
	}

	public removeDigitalSignagePlayer(player: PlayerMapping) {
		this.data.trainingRoom.digitalSignageDeviceIds.splice(this.data.trainingRoom.digitalSignageDeviceIds.indexOf(player.id), 1);
		this.finalDigitalSignagePlayers.splice(this.finalDigitalSignagePlayers.indexOf(player), 1);
		/*this.data.trainingRoom.digitalSignagePlayers.splice(this.data.trainingRoom.digitalSignagePlayers.indexOf(player), 1);*/
		this.resetFilters();
	}

	private resetFilters() {
		this.availableDebriefingPlayerList = null;
		this.usedDebriefingPlayerList = null;
		this.availableMediaScreenPlayerList = null;
		this.usedMediaScreenPlayerList = null;
		this.availableDigitalSignagePlayerList = null;
		this.usedDigitalSignagePlayerList = null;
	}

	public debriefingPlayerSelectionChanged(event: any) {
		this.addDebriefingPlayer(this.selectedDebriefingPlayer);
	}

	public mediaScreenSelectionChanged(event: any) {
		this.addMediaScreenPlayer(this.selectedMediaScreenPlayer);
	}

	public digitalSignageSelectionChanged(event: any) {
		this.addDigitalSignagePlayer(this.selectedDigitalSignagePlayer);
	}

	public getPlayerName(player: PlayerMapping): string {
		return player.name;
	}
}
